import NextLink from 'next/link';
import { cn } from '../../../lib/utils';
import { isExternalLink } from '..';

/* ============================================================================
   Item do menu lateral - visual novo (shadcn + Tailwind).

   Substitui a versao MUI (ListItemStyle/ListItemTextStyle/ListItemIconStyle de
   ./style.js, tres `styled()` que liam seis escalas do tema para desenhar uma
   linha com icone e texto).

   TRES REGRAS QUE ESTE COMPONENTE MATERIALIZA:

   1. ATIVO SE DISTINGUE POR PESO E FUNDO, nao so por cor. Item ativo pintado
      apenas de azul desaparece para quem tem baixa visao ou monitor ruim; aqui ele
      ganha fundo `primary/10`, texto `primary` e uma barra a esquerda.

   2. COLAPSADO MOSTRA O NOME NO `title`. Sidebar de 72px com so o icone e um teste
      de memoria; o tooltip nativo devolve o rotulo sem custo de componente.

   3. O ITEM COM FILHOS E UM <button>, nao um link. Ele nao navega - abre. Marcar
      como link faria o teclado e o leitor de tela prometerem navegacao que nao
      acontece.
   ============================================================================ */


/* LINHA DO MENU - 27/08/2026, "deixar mais moderna".

   Tres coisas mudaram, e cada uma tem um motivo concreto:

   1. `py-2` -> `py-1.5` e `gap-2.5` -> `gap-2`. Cada item tinha ~46px de altura, e
      com onze itens mais dois titulos de secao o menu passava do fim da tela em HD -
      aparecia rolagem numa lista que cabe. Em ~38px os onze cabem sem rolar.

   2. `text-[12.5px]` -> `text-[12px]`, o mesmo tamanho do resto do sistema. O menu
      era o unico lugar com 13px: um degrau de tamanho que ninguem pediu e que se
      nota quando o olho vai da lista para o menu.

   3. A pilula deixou de ocupar a largura inteira da coluna (`w-full` continua, mas
      o container agora tem margem lateral - ver index.jsx). Linha pintada de ponta
      a ponta e o desenho de menu de 2015; pilula recuada e o que os sistemas atuais
      usam, e ela separa visualmente o item do proprio limite da coluna. */
const LINHA_BASE =
  'group relative flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12px] font-semibold transition-colors';

export function NavItemRoot({ item, isCollapse, open = false, active, onOpen }) {
  const { title, path, icon, info, children } = item;

  const conteudo = (
    <>
      {/* A barra `bg-primary` de 3px que ficava aqui SAIU: com o item ativo pintado
          na propria cor primary, ela era um retangulo invisivel dentro de outro da
          mesma cor. */}

      {icon && (
        /* CHIP DE UMA COR SO.

           Tentei antes um tom por item (Home azul, Aplicativos violeta...). A cor
           por item distingue, mas dez hues numa coluna de 244px viram enfeite: o
           olho passa a procurar cor em vez de ler o rotulo, e a cor deixa de
           significar qualquer coisa no resto do sistema, onde ela indica ESTADO
           (ok, alerta, erro). Uma cor so - o destaque do sistema - mantem o chip
           como ancora visual sem competir com os tons que informam.

           O chip continua existindo: sem ele os icones flutuam soltos e a coluna
           perde o alinhamento vertical que faz varrer a lista com o olho. */
        <span
          className={cn(
            'grid h-6 w-6 flex-none place-items-center rounded-md transition-colors',
            // `/20` e `/5`, nao `/15`: a escala padrao de opacidade do Tailwind e
            // 0,5,10,20,25,30,40,50,... - `/15` nao existe e nao gera regra nenhuma,
            // entao o chip do item ativo sairia SEM fundo. Conferido no CSS
            // construido. `/20` tambem separa melhor o chip da pilula (`/10`) do
            // item ativo - com valores proximos o chip desaparecia dentro dela.
            // Ativo: o chip precisa se separar de um fundo que agora e a cor cheia,
            // entao ele clareia (`primary-foreground/20`) em vez de escurecer.
            // Inativo: fundo neutro com o icone na cor da paleta - assim o menu
            // mostra a cor escolhida em toda a coluna, e nao so na linha ativa.
            /* O FUNDO DO CHIP SO EXISTE NO ITEM ATIVO (27/08/2026).
               Inativo ele era `bg-soft`: onze retangulos cinzas empilhados numa
               coluna de 244px, um por item, competindo com o rotulo que a pessoa
               esta lendo. A CAIXA continua (mesmo tamanho, `place-items-center`),
               entao o alinhamento vertical dos icones - que e o que permite varrer
               a lista com o olho - nao se perde; o que saiu foi a pintura. */
            active ? 'bg-primary-foreground/20 text-primary-foreground' : 'text-primary group-hover:bg-soft',
            isCollapse && 'mx-auto'
          )}
        >
          <span className="grid h-[18px] w-[18px] place-items-center">{icon}</span>
        </span>
      )}

      {!isCollapse && <span className="min-w-0 flex-1 truncate">{title}</span>}

      {!isCollapse && (
        <>
          {info}
          {children && <Seta open={open} />}
        </>
      )}
    </>
  );

  // `primary` e token do shadcn (canais HSL), entao aceita o modificador de
  // opacidade - diferente dos tokens --gps-*, onde `/10` nao geraria regra.
  //
  // Declarado ANTES de `classes` de proposito: `const` nao e hoisted, e usar a
  // constante antes da linha que a declara lanca ReferenceError no render - o que
  // derruba a arvore inteira e deixa a tela branca, sem indicar a causa.
  /* ITEM ATIVO EM COR CHEIA (19/08/2026).
     Era `bg-primary/10 text-primary`, e na tela isso saia CINZA - 10% de um
     azul-marinho escuro sobre branco nao e um azul claro, e um cinza levemente
     azulado. Mesmo engano que eu cometi no cabecalho das tabelas e na faixa
     superior: tom fraco so parece colorido quando a cor de origem e saturada, e o
     primary padrao deste sistema e escuro.

     Agora o item ativo usa a cor cheia com o texto de contraste que o proprio tema
     define, igual a faixa superior e ao cabecalho das tabelas - as tres marcas de
     "aqui e o seu lugar" passam a ser a mesma coisa, em qualquer preset. */
  const classeAtivo = active ? 'bg-primary text-primary-foreground' : '';

  const classes = cn(
    LINHA_BASE,
    active ? cn('font-bold', classeAtivo) : 'text-muted-foreground hover:bg-soft hover:text-ink',
    isCollapse && 'justify-center px-0'
  );

  if (children) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className={classes}
        title={isCollapse ? title : undefined}
        aria-expanded={open}
      >
        {conteudo}
      </button>
    );
  }

  if (isExternalLink(path)) {
    return (
      <a
        href={path}
        target="_blank"
        rel="noopener noreferrer"
        className={classes}
        title={isCollapse ? title : undefined}
      >
        {conteudo}
      </a>
    );
  }

  return (
    <NextLink href={path} className={classes} title={isCollapse ? title : undefined}>
      {conteudo}
    </NextLink>
  );
}

// ----------------------------------------------------------------------

export function NavItemSub({ item, open = false, active = false, onOpen }) {
  const { title, path, info, children } = item;

  const conteudo = (
    <>
      <Ponto active={active} />
      <span className="min-w-0 flex-1 truncate">{title}</span>
      {info}
      {children && <Seta open={open} />}
    </>
  );

  const classes = cn(
    'flex w-full items-center gap-2.5 rounded-lg py-1.5 pl-3 pr-2.5 text-left text-[12px] transition-colors',
    /* O SUBITEM continua so em texto colorido, sem fundo cheio: ele vive DENTRO de
       um grupo cujo pai ja pode estar ativo, e dois blocos pintados aninhados fazem
       o olho procurar qual dos dois e a pagina atual. Aqui a cor no texto e o ponto
       maior ao lado bastam. */
    active ? 'font-bold text-primary' : 'font-medium text-muted-foreground hover:bg-soft hover:text-ink'
  );

  if (children) {
    return (
      <button type="button" onClick={onOpen} className={classes} aria-expanded={open}>
        {conteudo}
      </button>
    );
  }

  if (isExternalLink(path)) {
    return (
      <a href={path} target="_blank" rel="noopener noreferrer" className={classes}>
        {conteudo}
      </a>
    );
  }

  return (
    <NextLink href={path} className={classes}>
      {conteudo}
    </NextLink>
  );
}

// ----------------------------------------------------------------------

export function Ponto({ active }) {
  return (
    <span className="grid h-[22px] w-[22px] flex-none place-items-center">
      <span
        className={cn(
          'rounded-full transition-all',
          active ? 'h-2 w-2 bg-primary' : 'h-1 w-1 bg-faint'
        )}
      />
    </span>
  );
}

export function Seta({ open }) {
  return (
    <span
      className={cn('flex-none text-[10px] leading-none transition-transform', open && 'rotate-90')}
      aria-hidden
    >
      ▶
    </span>
  );
}
